import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Text, Card, Toggle } from '@teals/ui-web';
import { useThemeToggle } from '../../hooks/useThemeToggle';
import {
  useGetPreferencesQuery,
  useUpdatePreferencesMutation,
  type NotificationPreferences,
} from '../../store/api';

const PageContainer = styled.div`
  max-width: 640px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const SectionCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const SectionIntro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const ThemeOptions = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const ThemeOption = styled.button<{ $active: boolean }>`
  flex: 1;
  min-width: 140px;
  padding: 1rem;
  border: 1px solid
    ${({ $active }) => ($active ? 'var(--accent)' : 'var(--border, #e2e8f0)')};
  border-radius: 10px;
  background: ${({ $active }) =>
    $active ? 'var(--accent-subtle, rgba(45, 154, 143, 0.12))' : 'var(--bg-elevated, #f8fafc)'};
  color: ${({ $active }) =>
    $active ? 'var(--accent)' : 'var(--text-secondary, #64748b)'};
  box-shadow: ${({ $active }) =>
    $active ? 'inset 0 0 0 1px var(--accent)' : 'none'};
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  transition: all 0.2s;

  &:hover {
    border-color: var(--accent);
    color: ${({ $active }) =>
      $active ? 'var(--accent)' : 'var(--text-primary, #0f172a)'};
  }
`;

const ThemeIcon = styled.span`
  font-size: 1.25rem;
  line-height: 1;
`;

const ThemeLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
`;

const ThemeHint = styled.span`
  font-size: 0.75rem;
  font-weight: 400;
  color: var(--text-muted, #94a3b8);
`;

const PrefList = styled.div`
  display: flex;
  flex-direction: column;
`;

const PrefRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.875rem 0;
  border-bottom: 1px solid var(--border, #e2e8f0);

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const PrefCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
`;

const DEFAULT_NOTIFICATIONS: NotificationPreferences = {
  productUpdates: true,
  billingAlerts: true,
  agentActivity: true,
  marketing: false,
};

const Settings: React.FC = () => {
  const { mode, set: setMode } = useThemeToggle();
  const { data: preferences } = useGetPreferencesQuery();
  const [updatePreferences] = useUpdatePreferencesMutation();
  const [notifications, setNotifications] = useState<NotificationPreferences>(
    DEFAULT_NOTIFICATIONS
  );

  useEffect(() => {
    const n = preferences?.notifications;
    if (!n) return;
    setNotifications((prev) => {
      if (
        prev.productUpdates === n.productUpdates &&
        prev.billingAlerts === n.billingAlerts &&
        prev.agentActivity === n.agentActivity &&
        prev.marketing === n.marketing
      ) {
        return prev;
      }
      return n;
    });
  }, [
    preferences?.notifications?.productUpdates,
    preferences?.notifications?.billingAlerts,
    preferences?.notifications?.agentActivity,
    preferences?.notifications?.marketing,
  ]);

  const handleThemeChange = (next: 'light' | 'dark') => {
    setMode(next);
    void updatePreferences({ theme: next });
  };

  const updatePref = (key: keyof NotificationPreferences, value: boolean) => {
    setNotifications((prev) => ({ ...prev, [key]: value }));
    void updatePreferences({ notifications: { [key]: value } });
  };

  return (
    <PageContainer>
      <PageHeader>
        <Text variant="h2">Settings</Text>
        <Text variant="body1" color="secondary">
          Appearance and notification preferences for your account
        </Text>
      </PageHeader>

      <SectionCard padding={24}>
        <SectionIntro>
          <Text variant="h4">Appearance</Text>
          <Text variant="caption" color="secondary">
            Choose how Luminafactory looks across the portal.
          </Text>
        </SectionIntro>

        <ThemeOptions role="radiogroup" aria-label="Colour theme">
          <ThemeOption
            type="button"
            $active={mode === 'light'}
            role="radio"
            aria-checked={mode === 'light'}
            onClick={() => handleThemeChange('light')}
          >
            <ThemeIcon aria-hidden="true">{'\u2600'}</ThemeIcon>
            <ThemeLabel>Light</ThemeLabel>
            <ThemeHint>Bright surfaces</ThemeHint>
          </ThemeOption>
          <ThemeOption
            type="button"
            $active={mode === 'dark'}
            role="radio"
            aria-checked={mode === 'dark'}
            onClick={() => handleThemeChange('dark')}
          >
            <ThemeIcon aria-hidden="true">{'\u263E'}</ThemeIcon>
            <ThemeLabel>Dark</ThemeLabel>
            <ThemeHint>Low-light surfaces</ThemeHint>
          </ThemeOption>
        </ThemeOptions>
      </SectionCard>

      <SectionCard padding={24}>
        <SectionIntro>
          <Text variant="h4">Notifications</Text>
          <Text variant="caption" color="secondary">
            Control which updates you receive. Preferences apply to this account.
          </Text>
        </SectionIntro>

        <PrefList>
          <PrefRow>
            <PrefCopy>
              <Text variant="body1">Product updates</Text>
              <Text variant="caption" color="secondary">
                Email about new portal features and product news
              </Text>
            </PrefCopy>
            <Toggle
              value={notifications.productUpdates}
              onChange={(value) => updatePref('productUpdates', value)}
            />
          </PrefRow>

          <PrefRow>
            <PrefCopy>
              <Text variant="body1">Billing alerts</Text>
              <Text variant="caption" color="secondary">
                Invoices due, payment failures, and budget threshold alerts
              </Text>
            </PrefCopy>
            <Toggle
              value={notifications.billingAlerts}
              onChange={(value) => updatePref('billingAlerts', value)}
            />
          </PrefRow>

          <PrefRow>
            <PrefCopy>
              <Text variant="body1">Agent activity</Text>
              <Text variant="caption" color="secondary">
                In-app alerts for notable agent and session events
              </Text>
            </PrefCopy>
            <Toggle
              value={notifications.agentActivity}
              onChange={(value) => updatePref('agentActivity', value)}
            />
          </PrefRow>

          <PrefRow>
            <PrefCopy>
              <Text variant="body1">Marketing & tips</Text>
              <Text variant="caption" color="secondary">
                Occasional tips and marketing emails
              </Text>
            </PrefCopy>
            <Toggle
              value={notifications.marketing}
              onChange={(value) => updatePref('marketing', value)}
            />
          </PrefRow>
        </PrefList>
      </SectionCard>
    </PageContainer>
  );
};

export default Settings;
